import type { FormQuestion, PublicFormVersion } from '@kushya/shared';

import type { StoredFormDraft } from '@/lib/form-draft-storage';
import type { AnswerRecord } from './types';

export function getDraftIdentity(form?: PublicFormVersion) {
  if (!form) {
    return undefined;
  }
  return {
    projectId: form.projectId,
    formId: form.formId,
    version: form.version,
  };
}

export function getQuestionRefSet(questions: FormQuestion[]) {
  return new Set(questions.map((q) => q.ref));
}

export function filterAnswersToKnownQuestions(
  draftAnswers: Record<string, unknown> | undefined,
  questionRefs: Set<string>,
): AnswerRecord {
  const nextAnswers: AnswerRecord = {};
  for (const [key, value] of Object.entries(draftAnswers ?? {})) {
    if (questionRefs.has(key)) {
      nextAnswers[key] = value;
    }
  }
  return nextAnswers;
}

export function sanitizeQuestionPath(
  questionPath: unknown,
  questionRefs: Set<string>,
): string[] {
  if (!Array.isArray(questionPath)) {
    return [];
  }
  return questionPath.filter(
    (ref): ref is string => typeof ref === 'string' && questionRefs.has(ref),
  );
}

export function sanitizeQuestionHistory(
  history: unknown,
  totalQuestions: number,
): number[] {
  if (!Array.isArray(history)) {
    return [];
  }
  return history.filter(
    (step): step is number =>
      Number.isInteger(step) && step >= 0 && step < totalQuestions,
  );
}

export function sanitizePageHistory(history: unknown, totalPages: number): number[] {
  if (!Array.isArray(history)) {
    return [];
  }
  return history.filter(
    (index): index is number =>
      Number.isInteger(index) && index >= 0 && index < totalPages,
  );
}

export function getHasDraftProgress(params: {
  answers: AnswerRecord;
  hasIntro: boolean;
  isOnIntro: boolean;
  hasPages: boolean;
  currentPageIndex: number;
  currentStep: number;
  questionHistoryLength: number;
  pageHistoryLength: number;
}) {
  const {
    answers,
    hasIntro,
    isOnIntro,
    hasPages,
    currentPageIndex,
    currentStep,
    questionHistoryLength,
    pageHistoryLength,
  } = params;

  const hasAnswers = Object.keys(answers).length > 0;
  const startedFromIntro = hasIntro && !isOnIntro;
  const advanced = hasPages ? currentPageIndex > 0 : currentStep > 0;
  const hasHistory = questionHistoryLength > 0 || pageHistoryLength > 0;

  return hasAnswers || startedFromIntro || advanced || hasHistory;
}

export function buildDraftSnapshot(params: {
  form: PublicFormVersion;
  createdAt: string;
  nowIso: string;
  startedAtIso: string;
  answers: AnswerRecord;
  currentStep: number;
  currentPageIndex: number;
  questionHistory: number[];
  pageHistory: number[];
  questionPath: string[];
}): StoredFormDraft {
  const {
    form,
    createdAt,
    nowIso,
    startedAtIso,
    answers,
    currentStep,
    currentPageIndex,
    questionHistory,
    pageHistory,
    questionPath,
  } = params;

  return {
    schemaVersion: 1,
    projectId: form.projectId,
    formId: form.formId,
    version: form.version,
    createdAt,
    updatedAt: nowIso,
    startedAt: startedAtIso,
    answers,
    currentStep,
    currentPageIndex,
    questionHistory,
    pageHistory,
    questionPath,
  };
}

